import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationExtras, Params, Router, Event} from '@angular/router';
import {Store} from '@ngxs/store';
import {Location} from '@angular/common';
import {Navigate} from '@ngxs/router-plugin';
import {Observable} from 'rxjs';
import {Article, ReportNavigation, VehicleQueryParams} from '@shared/interfaces';
import {isPlatformServer} from '@angular/common';
import {DOCUMENT} from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class RoutingService {

  constructor(private store: Store, private router: Router, @Inject(PLATFORM_ID) private platformId: any,
              @Inject(DOCUMENT) private document: any, private location: Location) {
  }

  getRouterEvents(): Observable<Event> {
    return this.router.events;
  }

  getCurrentUrl(): string {
    return this.router.url;
  }

  getHost(): string {
    if (isPlatformServer(this.platformId)) {
      return this.document.location.host;
    }
    return window.location.host;
  }

  navigateHome(): Observable<any> {
    return this.store.dispatch(new Navigate(['/explore']));
  }

  navigateLogin(args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['/login'], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateRegister(args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['/register'], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToPreviousPage(): Observable<any> {
    const previousUrl = this.location.getState();
    if (previousUrl && previousUrl['navigationId']) {
      this.location.back();
    } else {
      return this.store.dispatch(new Navigate(['/explore']));
    }
  }

  navigateCreateReport(args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['/create-research'], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToQuickReport(reportId: string, args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['/q', reportId], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToAnyReport(reportData: ReportNavigation) {
    let navRoute = '/r';
    if (reportData.informationLevel === 'quick') {
      navRoute = '/q';
    }
    return this.store.dispatch(new Navigate([navRoute, reportData.reportId], {
      ...reportData.metadata
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToReport(reportId: string, args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['/r', reportId], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToArticle(article: Article, args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['blog', article.category, article.url], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToCategory(category: string, args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['blog', category], {
      ...args
    }, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToReports(args?: any): Observable<any> {
    return this.store.dispatch(new Navigate(['view-all-research'], {
      ...args
    }));
  }

  navigateToVehicle(vehicleId: string, params: VehicleQueryParams): Observable<any> {
    return this.store.dispatch(new Navigate(['vehicle', vehicleId], params, {
      queryParamsHandling: 'merge'
    }));
  }

  navigateToBilling() {
    return this.store.dispatch(new Navigate(['/billing']));
  }

  decodeAndNavigateToPath(path: string) {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    const params = {};
    let match;
    // tslint:disable-next-line:no-conditional-assignment
    while (match = regex.exec(path)) {
      params[match[1]] = match[2];
    }
    if (path) {
      path = path.split('?')[0];
      if (path.includes('http') && path.includes('://')) {
        // Remove http:// or https:// and the domain.com
        path = path.replace(/^.*\/\/[^/]+/, '');
      }
      return this.navigateToPath([path], params);
    }
    return this.navigateHome();
  }

  navigateToPath(path: string[], query?: Params | undefined, extras?: NavigationExtras) {
    return this.store.dispatch(new Navigate(path, query, extras));
  }
}
